import { BehaviorSubject, catchError, map, Observable, of, ReplaySubject } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from '@angular/core';

import { ForgotPasswordOutcome, forgotPasswordOutcome } from "./forgot-password-outcome.util";
import { IUserAuthenticated } from "@data/interfaces/iuser-authenticated.interface";
import { ApiClass } from "@data/class/api-class";
import { environment } from "@env/environment";

interface ModulePermission {
	module: string;
	permissions: string[];
}

@Injectable({
	providedIn: 'root'
})
export class AuthService extends ApiClass {
	private currentUserSubject: BehaviorSubject<IUserAuthenticated | null>
	public currentUser: Observable<IUserAuthenticated | null>
	public constLocalUserVariable = 'renda-user'
	private userPermissions: ModulePermission[] = [];
	/** Refresco compartido: ver `refrescarSesion()`. `null` = no hay ninguno corriendo. */
	private refrescoEnVuelo: Observable<boolean> | null = null;

	constructor() {
		super();
		this.path = 'auth'
		const storedUser = JSON.parse(<string>localStorage.getItem(this.constLocalUserVariable));
		this.currentUserSubject = new BehaviorSubject<IUserAuthenticated | null>(storedUser);
		this.currentUser = this.currentUserSubject.asObservable();

		if (storedUser && storedUser.user && storedUser.user.permissions) {
			this.userPermissions = storedUser.user.permissions.modules
		}
	}

	public get getUser(): IUserAuthenticated | null {
		return this.currentUserSubject.value
	}

	public get getToken(): string {
		return this.currentUserSubject.value?.token ?? ''
	}

	// ADR-0014: el usuario interno debe cambiar su contraseña temporal en el primer
	// ingreso. El AuthGuard lee este flag y redirige a /change-password.
	public get mustChangePassword(): boolean {
		return !!this.currentUserSubject.value?.user?.mustChangePassword
	}

	// Limpia el flag tras un cambio exitoso de contraseña (subject + localStorage).
	clearMustChangePassword() {
		const current = this.currentUserSubject.value
		if (!current) return
		const updated: IUserAuthenticated = {
			...current,
			user: { ...current.user, mustChangePassword: false }
		}
		this.currentUserSubject.next(updated)
		this.updateUserLocalData(updated)
	}

	// "Olvidé mi contraseña" para usuarios internos (por correo). El backend genera
	// una temporal, marca mustChangePassword y la envía por correo. Respuesta genérica
	// (no revela si el correo existe). Endpoint público — el JwtInterceptor lo exime.
	//! B-006 · No usa `postCollection`: ese convierte cualquier error en una respuesta normal y pierde
	//! el código HTTP, así que un 429 (más de 3 por minuto) o un 400 se le mostraban a la persona como
	//! «te enviamos una contraseña temporal». Aquí el código decide qué se le dice.
	forgotPassword(email: string): Observable<ForgotPasswordOutcome> {
		return this.http.post(`${environment.apiUrl}${this.path}/forgot-password`, { email }).pipe(
			map((): ForgotPasswordOutcome => 'sent'),
			catchError((error: HttpErrorResponse) => of(forgotPasswordOutcome(error?.status)))
		)
	}

	// Cambia la contraseña del usuario autenticado. POST /auth/change-password.
	changePassword(currentPassword: string, newPassword: string) {
		return this.postCollection({ currentPassword, newPassword }, 'change-password')
	}

	login(data: { email?: string, identifier?: string, password: string }) {
		// El backend espera 'username' en lugar de 'email' para soportar login por email o teléfono
		// El formulario puede enviar 'email' o 'identifier' dependiendo del componente
		const usernameValue = data.email || data.identifier || ''
		const payload = { username: usernameValue, password: data.password }
		return this.postCollection(payload, 'login').pipe(
			map((r: { error: boolean; msg: string; data: any }) => {
				if (!r.error && r.data) {
					const user: IUserAuthenticated = {
						token: r.data.access_token,
						detailToken: {
							refreshToken: r.data.refresh_token,
							accessToken: r.data.access_token,
							expirationTime: 3000000
						},
						user: {
							id: r.data.user._id,
							names: {
								firstName: r.data.user.names,
								lastName: r.data.user.lastNames
							},
							email: r.data.user.email,
							mustChangePassword: r.data.user.mustChangePassword,
							isDev: r.data.user.isDev,
							permissions: {
								name: r.data.user.role.name,
								modules: r.data.user.role.modules.map((p: any) => ({
									module: p.module,
									permissions: p.permissions || []
								}))
							}
						}
					};
					this.currentUserSubject.next(user);
					this.updateUserLocalData(user);
					this.userPermissions = user.user.permissions.modules.map((p: any) => ({
						module: p.module,
						permissions: p.permissions || []
					}));
				}

				return r;
			})
		);
	}

	logout() {
		localStorage.removeItem(this.constLocalUserVariable)
		this.currentUserSubject.next(null)
		this.userPermissions = [];
		this.refrescoEnVuelo = null
	}

	// ─────────── Renovación de la sesión (B-044) ───────────

	/**
	 * Guarda el par de tokens rotado. Lo llama el `JwtInterceptor` cuando el backend los devuelve en
	 * las cabeceras de la respuesta, y `refrescarSesion()` con lo que responde `/auth/refresh`.
	 *
	 * Sin esto el navegador conservaba el access token vencido **para siempre**: el backend refrescaba
	 * en cada petición y se quedaba el token nuevo, así que la sesión quedaba colgando de que la
	 * cabecera `refresh_token` llegara íntegra una y otra vez.
	 */
	guardarTokens(accessToken: string, refreshToken?: string) {
		const actual = this.currentUserSubject.value
		if (!actual || !accessToken) return

		const actualizado: IUserAuthenticated = {
			...actual,
			token: accessToken,
			detailToken: {
				...actual.detailToken,
				accessToken,
				//* El refresco solo se pisa si vino uno nuevo: `/auth/refresh` devuelve los dos, pero
				//* una respuesta que solo rote el access no debe dejar la sesión sin con qué renovar.
				refreshToken: refreshToken || actual.detailToken.refreshToken,
			},
		}

		this.currentUserSubject.next(actualizado)
		this.updateUserLocalData(actualizado)
	}

	/**
	 * Renueva la sesión contra `POST /auth/refresh`, que es **público** y recibe el token en el
	 * CUERPO — no depende de que una cabecera con guion bajo sobreviva a los proxies de por medio.
	 *
	 * Devuelve `true` si la sesión quedó viva. Un `false` significa que de verdad murió (el refresco
	 * venció, o al usuario lo dieron de baja) y ahí sí toca mandar al login.
	 *
	 * **Una sola llamada en vuelo.** Cuando el token vence, las peticiones que estén corriendo fallan
	 * todas a la vez; sin compartir la llamada se dispararían N refrescos en paralelo, cada uno
	 * rotando sobre el anterior, y el último ganaría dejando a los demás con un token ya sustituido.
	 */
	refrescarSesion(): Observable<boolean> {
		if (this.refrescoEnVuelo) return this.refrescoEnVuelo

		const refreshToken = this.currentUserSubject.value?.detailToken?.refreshToken
		if (!refreshToken) return of(false)

		//* Se arranca YA y se reparte el resultado por un sujeto, en vez de encadenar un `shareReplay`:
		//* ese no dispara nada hasta que alguien se suscribe, así que dos llamadas seguidas antes de
		//* la primera suscripción abrían dos refrescos. Aquí la llamada sale una sola vez, la pidan
		//* cuando la pidan.
		const resultado = new ReplaySubject<boolean>(1)
		//! Se devuelve ESTA referencia y no el campo: con una respuesta que llegue de inmediato, el
		//! `subscribe` de abajo corre antes del `return` y ya dejó el campo en `null`. Devolver el
		//! campo entregaba `null` y reventaba a quien se suscribiera. Lo encontró la prueba.
		const compartido = resultado.asObservable()
		this.refrescoEnVuelo = compartido

		this.postCollection({ refreshToken }, 'refresh')
			.pipe(
				map((r: { error: boolean; data: any }) => {
					//* `postCollection` ya desenvuelve el `{ error, msg, data }` global de la API. El canal
					//* digital se estrelló justo aquí: sin desenvolver, los dos tokens salían `undefined` y
					//* el refresco «funcionaba» sin guardar nada.
					const accessToken = r?.data?.access_token
					if (r?.error || !accessToken) return false
					this.guardarTokens(accessToken, r.data.refresh_token)
					return true
				}),
				catchError(() => of(false))
			)
			.subscribe((ok) => {
				//! Se suelta ANTES de avisar, y el orden importa: quien reciba este resultado puede pedir
				//! otro refresco en su propio manejador —el interceptor reintenta ahí mismo—, y si el
				//! candado siguiera puesto se llevaría el resultado viejo en vez de uno nuevo.
				this.refrescoEnVuelo = null
				resultado.next(ok)
				resultado.complete()
			})

		return compartido
	}

	userHasAuthorization(module: string, permission: string) {
		return this.getUser?.user.permissions
			? (!!this.getUser?.user?.permissions.modules.find(p => p.module === module && p.permissions.includes(permission)))
			: false
	}

	updateUserLocalData(user: IUserAuthenticated) {
		if (user) {
			localStorage.setItem(this.constLocalUserVariable, JSON.stringify(user))
		}
	}

	hasModulePermission(module: string, permission: string): boolean {
		// Usar el método más confiable que ya funciona
		return this.userHasAuthorization(module, permission);
	}
}
